import { forwardRef, useContext, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ForwardedRef, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { requestFormCancel } from './formCancel';
import { OverlayDepth } from './overlayContext';
import { focusAvailable, focusElement, isTopOverlay, registerOverlay, tabbables } from './overlayManager';
import { ConfirmDialog } from './ConfirmDialog';
import type { WithClassName } from './types';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
export type CloseReason = 'escape' | 'backdrop' | 'cancel' | 'close-button';
export type OverlayProps = WithClassName & {
  open: boolean; title: ReactNode; ariaLabel?: string; description?: string;
  children: ReactNode; footer?: ReactNode; size?: ModalSize;
  dirty?: boolean; busy?: boolean; dismissable?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>; returnFocusRef?: RefObject<HTMLElement | null>;
  onClose: (reason: CloseReason) => void; onDiscard?: () => void;
};
export type DrawerProps = OverlayProps & { side?: 'right' | 'left'; /** Legacy common/Drawer layout override. Prefer size in new code. */ width?: string };
type PrimitiveProps = DrawerProps & { kind: 'modal' | 'drawer'; role?: 'dialog' | 'alertdialog'; descriptionId?: string; showClose?: boolean };

/** Shared implementation; public Modal/Drawer and ConfirmDialog all use this stack. */
export const Overlay = forwardRef<HTMLDivElement, PrimitiveProps>(function Overlay({ open, ...props }, ref) {
  return open ? <OpenOverlay {...props} forwardedRef={ref} /> : null;
});

function OpenOverlay({ title, ariaLabel, description, children, footer, size = 'md', dirty = false, busy = false,
  dismissable = true, initialFocusRef, returnFocusRef, onClose, onDiscard, className, kind, side = 'right', width,
  role = 'dialog', descriptionId, showClose = true, forwardedRef }: Omit<PrimitiveProps, 'open'> & { forwardedRef: ForwardedRef<HTMLDivElement> }) {
  const id = useId();
  const depth = useContext(OverlayDepth);
  const [host] = useState(() => document.createElement('div'));
  const [pending, setPending] = useState<CloseReason | null>(null);
  const dialog = useRef<HTMLDivElement | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const opener = useRef(document.activeElement instanceof HTMLElement && document.activeElement !== document.body
    && document.activeElement !== document.documentElement ? document.activeElement : null);
  const latest = useRef({ initialFocusRef, returnFocusRef, onClose, onDiscard, busy, dismissable, dirty });
  latest.current = { initialFocusRef, returnFocusRef, onClose, onDiscard, busy, dismissable, dirty };

  function requestClose(reason: CloseReason) {
    const current = latest.current;
    if (!isTopOverlay(host) || current.busy || !current.dismissable) return;
    const forms = [...dialog.current!.querySelectorAll<HTMLFormElement>('form[data-ui-form]')];
    const finish = () => {
      if (current.dirty && !forms.length) setPending(reason);
      else current.onClose(reason);
    };
    // Every owning Form gets a chance to preserve its draft, including busy forms.
    const guard = (index: number) => {
      const form = forms[index];
      if (form) requestFormCancel(form.id, () => guard(index + 1));
      else finish();
    };
    guard(0);
  }
  const close = useRef(requestClose);
  close.current = requestClose;

  useLayoutEffect(() => {
    host.className = `ui-overlay-layer ui-overlay-layer--${kind}`;
    return registerOverlay({ host, dialog: dialog.current!, depth, opener: opener.current, lastFocus: null,
      returnFocus: () => latest.current.returnFocusRef?.current ?? null,
      close: () => close.current('escape'),
      initialFocus: () => {
        const explicit = latest.current.initialFocusRef?.current;
        if (explicit && dialog.current?.contains(explicit) && focusAvailable(explicit)) { focusElement(explicit); return; }
        if (body.current && body.current.scrollHeight > body.current.clientHeight) { heading.current?.focus(); return; }
        const first = body.current && tabbables(body.current)[0];
        (first || (dialog.current && tabbables(dialog.current)[0]) || dialog.current)?.focus();
      },
    });
  }, [host, kind, depth]);

  return createPortal(<OverlayDepth.Provider value={depth + 1}>
    <div className={`ui-overlay-backdrop ui-overlay-backdrop--${kind} ui-overlay-backdrop--${side}`}
      onClick={(event) => { event.stopPropagation(); if (event.target === event.currentTarget) requestClose('backdrop'); }}>
      <div ref={(node) => { dialog.current = node; if (typeof forwardedRef === 'function') forwardedRef(node); else if (forwardedRef) forwardedRef.current = node; }}
        className={`ui-overlay ui-overlay--${kind} ui-overlay--${size} ${className ?? ''}`}
        style={{ width: kind === 'drawer' ? width : undefined }}
        role={role} aria-modal="true" aria-labelledby={`${id}-title`}
        aria-label={typeof title !== 'string' ? ariaLabel : undefined}
        aria-describedby={description ? `${id}-description` : descriptionId} aria-busy={busy || undefined} tabIndex={-1}>
        <header className="ui-overlay__header">
          <h2 ref={heading} id={`${id}-title`} aria-label={typeof title !== 'string' ? ariaLabel : undefined} tabIndex={-1}>{title}</h2>
          {dismissable && showClose && <button type="button" className="ui-button ui-button--ghost" aria-label="Close"
            disabled={busy} onClick={() => requestClose('close-button')}>✕</button>}
        </header>
        <div ref={body} className="ui-overlay__body">
          {description && <p id={`${id}-description`}>{description}</p>}
          {children}
        </div>
        {footer && <footer className="ui-overlay__footer">{footer}</footer>}
      </div>
    </div>
    {pending && <ConfirmDialog open title="Discard changes?" description="Your unsaved changes will be lost."
      level="warning" confirmLabel="Discard changes" cancelLabel="Keep editing" busy={false}
      onCancel={() => setPending(null)} onConfirm={async () => {
        const reason = pending; setPending(null); latest.current.onDiscard?.(); latest.current.onClose(reason);
      }} />}
  </OverlayDepth.Provider>, host);
}

export const Modal = forwardRef<HTMLDivElement, OverlayProps>(function Modal(props, ref) {
  return <Overlay {...props} ref={ref} kind="modal" />;
});
export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(props, ref) {
  return <Overlay {...props} ref={ref} kind="drawer" size={props.size ?? 'sm'} />;
});
